import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Button, DateRangeSelector, ToastMessage } from '@cokitchen/cokitchen-components';
import { BrandType } from 'models/brand.model';
import { ReviewType } from 'models/review.model';
import ReviewCard from 'components/cs/review/review-card';
import MarkAsResolvedModal from 'components/cs/review/mark-as-resolved-modal';
import { apiService } from 'utils/api';

import { ExportAltIcon } from 'cokitchen-icons';
import ExportModal from 'components/cs/modals/export-modal';
import { toast } from 'react-toastify';
import useRoleContext from 'contexts/role.context';
import { ENUM_ROLE } from '@cokitchen/cokitchen-auth';
import useDashboardContext from 'contexts/dashboard.context';

type FiltersType = {
  startDate: Date;
  endDate: Date;
};
type Props = {
  activeBrand: BrandType;
  setActiveBrand: (brand: BrandType) => void;
  allBrands: BrandType[];
  filters: FiltersType;
  setFilters: (filters: FiltersType) => void;
  rating: number | null;
};

type ReviewPayloadType = {
  email: string;
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
};
const ALL_TABS = {
  all: 'All',
  done: 'Resolved',
  pending: 'Unresolved'
} as const;

const navStyles = {
  active: 'border-b border-b-[4px] border-b-[#E60A2B] mb-0 text-[#1A1A1A]',
  inactive: 'text-[#858585]'
};

const baseUrl = process.env.REACT_APP_ORDER_STATS_API_KEY;
const BrandReviews: FC<Props> = ({ activeBrand, setActiveBrand, filters, setFilters, rating }) => {
  const isMounted = useRef(false);
  const [status, setStatus] = useState<keyof typeof ALL_TABS>('all');
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState<ReviewType[]>([]);
  const [showMarkModal, setShowMarkModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState({} as ReviewType);
  const [hasMore, setHasMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalNumber, setTotalNumber] = useState(0);
  const [showExportReviewModal, setShowExportReviewModal] = useState(false);
  const { role } = useRoleContext();
  const { activeCokitchen } = useDashboardContext();

  const reviewsWithBrand = useMemo(() => {
    return reviews.filter((review) => review.brand);
  }, [reviews]);

  const fetchSingleBrandReviews = async (pageNo: number) => {
    try {
      setLoading(true);
      const { startDate: start_date, endDate: end_date } = filters;
      const params: any = {
        start_date,
        end_date
      };
      if (rating) params.rating = rating;
      if (status !== 'all') params.status = status;
      const res = await apiService.get(`marketing/brand/one/reviews/${activeBrand.id}/${pageNo}`, {
        baseUrl,
        params
      });
      const { brand_reviews } = res.data;
      const revs = pageNo === 1 ? brand_reviews.results : [...reviews, ...brand_reviews.results];
      setReviews(revs);

      setTotalNumber(brand_reviews.total);
    } finally {
      isMounted.current = true;
      setLoading(false);
    }
  };

  const fetchAllBrandReviews = async (pageNo: number) => {
    try {
      setLoading(true);
      const { startDate: start_date, endDate: end_date } = filters;
      const params: any = {
        start_date,
        end_date
      };
      if (rating) params.rating = rating;
      if (status !== 'all') params.status = status;
      const res = await apiService.get(
        `marketing/brand/reviews/${activeCokitchen.id}/${pageNo}`,

        { params, baseUrl }
      );

      const brand_reviews = res.data?.data?.brand_reviews;

      const revs = pageNo === 1 ? brand_reviews.results : [...reviews, ...brand_reviews.results];

      setReviews(revs);
      setTotalNumber(brand_reviews.total);
    } finally {
      isMounted.current = true;
      setLoading(false);
    }
  };

  const fetchBrandReviews = (defaultPage?: number) => {
    const pageNo = defaultPage ?? pageNumber;
    if (activeBrand.id === 'ALL') {
      fetchAllBrandReviews(pageNo);
      return;
    }
    fetchSingleBrandReviews(pageNo);
  };

  useEffect(() => {
    fetchBrandReviews();
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      setPageNumber(1);
      fetchBrandReviews(1);
    }
  }, [status, filters, rating]);

  useEffect(() => {
    setHasMore(reviews.length < totalNumber);
  }, [reviews]);

  useEffect(() => {
    if (pageNumber > 1) fetchBrandReviews();
  }, [pageNumber]);

  const openMarkModal = (review: ReviewType) => {
    setShowMarkModal(true);
    setSelectedReview(review);
  };

  const fetchMoreData = () => {
    setPageNumber((p) => p + 1);
  };

  const updateResolvedIssue = () => {
    const reviewIndex = reviews.findIndex((r) => r.id === selectedReview.id);
    const updatedReview: ReviewType = {
      ...reviews[reviewIndex],
      done: true,
      pending: false,
      rate: '5.00'
    };
    const otherReviews = reviews.filter((r) => r.id !== selectedReview.id);
    otherReviews.splice(reviewIndex, 0, updatedReview);
    setReviews(otherReviews);
  };

  const exportReviews = async ({ email, dateRange }: ReviewPayloadType) => {
    try {
      await apiService.get(`cs/export/review`, {
        params: {
          email: email,
          start_date: dateRange.startDate,
          end_date: dateRange.endDate
        },
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      toast(<ToastMessage type='success' message='Reviews exported successfully' />);
      setShowExportReviewModal(false);
    } catch (err) {
      toast(<ToastMessage type='error' message='Failed to export Reviews' />);
    }
  };
  const isCSSuperAdmin = role === ENUM_ROLE.CS_SUPER_ADMIN;

  return (
    <div>
      {activeBrand.id !== 'ALL' && (
        <p className='font-medium text-lg mb-4'>
          Showing reviews for {activeBrand?.name} ({totalNumber})
        </p>
      )}
      <div className='flex justify-between items-center gap-4 border-b border-gray-border mb-6'>
        <ul className='flex justify-start no-scroll-bar grow'>
          {Object.entries(ALL_TABS).map(([key, value], index) => (
            <li
              role='button'
              key={index}
              onClick={() => setStatus(key as any)}
              className={`flex mr-6 last:mr-0 cursor-pointer py-4 whitespace-nowrap
              ${key === status ? navStyles.active : navStyles.inactive}`}>
              {value}
            </li>
          ))}
        </ul>
        <div className='flex gap-2 items-center'>
          <DateRangeSelector showTime onChange={(v) => setFilters(v.date)} />
          {isCSSuperAdmin && (
            <Button
              color='primary'
              variant='block'
              small
              className='w-[139px] h-[40px] flex gap-1'
              onClick={() => setShowExportReviewModal(true)}>
              <ExportAltIcon />
              Export
            </Button>
          )}
        </div>
      </div>
      {reviews.length === 0 ? (
        <div className='text-2xl leading-[40vh] text-center'>
          {loading ? (
            'Loading...'
          ) : (
            <span>
              There are no reviews
              {status !== 'all' ? ' with the selected filter' : ''}
            </span>
          )}
        </div>
      ) : (
        <InfiniteScroll
          hasMore={hasMore}
          next={() => fetchMoreData()}
          dataLength={reviews.length}
          loader={<p className='text-center mt-8'>Loading more reviews...</p>}
          endMessage={<p className='text-center mt-8'>End of list</p>}>
          <div className='grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3'>
            {reviewsWithBrand.map((review) => (
              <ReviewCard
                key={review.id}
                review={review}
                brand={review.brand}
                isAllSelected={activeBrand.id === 'ALL'}
                isResolved={+review.rate > 4 && !review.pending}
                markIssue={() => openMarkModal(review)}
                onBrandSelect={() => setActiveBrand(review.brand)}
              />
            ))}
          </div>
        </InfiniteScroll>
      )}
      <MarkAsResolvedModal
        show={showMarkModal}
        onClose={() => setShowMarkModal(false)}
        onSuccess={updateResolvedIssue}
        review={selectedReview}
        itemType={activeBrand.item_type}
      />
      <ExportModal
        title='Export reviews'
        onClose={() => setShowExportReviewModal(false)}
        show={showExportReviewModal}
        onSubmit={(email, dateRange) =>
          exportReviews({
            email,
            dateRange
          })
        }
      />
    </div>
  );
};

export default BrandReviews;
